function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function formatCurrency(money, removeZeros = true) {
  const formatter = new Intl.NumberFormat(`${Shopify.locale}-${Shopify.country}`, {
    style: 'currency',
    currency: Shopify.currency.active,
  });
  return removeZeros ? formatter.format(money / 100).replace('.00', '') : formatter.format(money / 100);
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

async function addItemsToCart(items) {
  const body = { items: items };
  return fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
    .then((response) => {
      if (response.status == 422) {
        this.handleErrorMessage(response.description);
        return;
      }
      return response.json();
    })
    .catch((e) => {
      console.error(e);
    });
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll('button:not(.localization-selector)').forEach((button) =>
      button.addEventListener('click', this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
    if (event.target.form && !event.target.form.classList.contains('cart-drawer__form')) {
      document.querySelector('.product__info').querySelector('addon-product')?.updateTotalPrice();
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  // updateTotalPrice() {
  //   const regularPrice =
  //     parseFloat(
  //       document
  //         .querySelector('.product__info-wrapper')
  //         .querySelector('.price-item--regular')
  //         .textContent.replace('$', '')
  //         .replaceAll(',', '')
  //         .trim()
  //     ) * 100;
  //   const compareAtPrice =
  //     parseFloat(
  //       document
  //         .querySelector('.product__info-wrapper')
  //         .querySelector('.price__sale')
  //         ?.querySelector('.price-item--regular')
  //         .textContent.replace('$', '')
  //         .replaceAll(',', '')
  //         .trim()
  //     ) * 100;
  //   console.log(regularPrice, compareAtPrice);
  // }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', value <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.backdrop = this.querySelector('.backdrop');
    this.summart = this.querySelector('summary');
    if (!this.backdrop) return;
    this.backdrop.addEventListener('click', this.closeMenuDrawer.bind(this));
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) {
      elementToFocus = this.summary;
    }
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  triggerBackdrop() {}

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      // this.addEventListener('pointerup', (event) => {
      //   if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      // });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    // window.pauseAllMedia();
    if (this.classList.contains('product-popup-bundle-modal') && !window.matchMedia('(min-width: 40em)').matches) {
      const slider = this.querySelector('slider-component');
      slider.setSlidePosition(window.innerWidth);
    }
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    // window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = this.dataset.loop?.toLowerCase() === 'true' || false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.dotButtons = this.querySelectorAll('button[name="index"]');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.isAutoPlay = parseInt(this.dataset.autoplay, 10);
    this.isPlaying = false;

    if (!this.slider || !this.nextButton) return;

    this.initPages();

    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    if (this.isAutoPlay) {
      const intersectionObserver = new IntersectionObserver((entries) => this.autoPlay(entries));
      intersectionObserver.observe(this.slider, {
        root: null,
        rootMargin: '0px',
        threshold: 0.2,
      });
    }

    let options = {
      rootMargin: '0px',
      threshold: 0.5,
    };

    const callback = (entries, observer) => {
      entries.forEach((entry) => {
        const { target } = entry;

        if (entry.intersectionRatio >= 0.5) {
          target.classList.add('is-active');
        } else {
          target.classList.remove('is-active');
        }
      });
    };

    const scrollObserver = new IntersectionObserver(callback, options);

    this.sliderItems.forEach((slide, index) => {
      scrollObserver.observe(slide);
    });

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    if (this.dotButtons.length > 0) {
      this.dotButtons.forEach((button) => {
        button.addEventListener('click', this.onDotClick.bind(this));
      });
    }
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    // TODO: Fix this for full page sliders
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    // console.log(this.sliderItems.length, this.sliderItemsToShow.length, this.slidesPerPage, this.slider.clientWidth);
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    // TODO: bug with currentelement
    // this.sliderItems.forEach((el) => el.classList.remove('is-active'));
    // this.sliderItems[this.currentPage].classList.add('is-active');

    if (this.dotButtons.length > 0) {
      this.dotButtons.forEach((button) => {
        button.classList.remove('slider-counter__link--active');
      });

      if (this.dotButtons[this.currentPage - 1]) {
        this.dotButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
      }
    }

    if (this.dotButtons.length && this.dotButtons[0].parentElement.classList.contains('reviews')) {
      this.dotButtons[0].parentElement.style.setProperty(
        '--link-left',
        this.dotButtons[this.currentPage - 1].offsetLeft + 'px'
      );
      this.dotButtons[0].parentElement.style.setProperty(
        '--link-width',
        this.dotButtons[this.currentPage - 1].getBoundingClientRect().width + 'px'
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  autoPlay(entries) {
    // this.currentPage, this.totalPages
    if (!this.isAutoPlay) {
      return;
    }
    entries.forEach((entry) => {
      this.interval;
      if (entry.isIntersecting) {
        this.isPlaying = true;
        this.interval = window.setInterval(() => {
          if (this.currentPage < this.totalPages) {
            this.slider.scroll({
              top: 0,
              left: this.currentPage * this.sliderItemOffset,
            });
          } else {
            this.slider.scroll({
              top: 0,
              left: 0,
            });
          }
        }, this.isAutoPlay);
      } else {
        clearInterval(this.interval);
        this.isPlaying = false;
      }
    });
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    // console.log(this.currentPage, this.totalPages, this.sliderItemOffset, this.isSlideVisible);
    if (this.enableSliderLooping && this.currentPage === this.totalPages) {
      this.slideScrollPosition =
        event.currentTarget.name === 'next' ? 0 : this.slider.scrollLeft - step * this.sliderItemOffset;
      this.resetPages();
    } else if (this.enableSliderLooping && this.currentPage === 1 && event.currentTarget.name === 'previous') {
      this.slideScrollPosition = this.totalPages * (this.sliderItemOffset * step);
    } else {
      this.slideScrollPosition =
        event.currentTarget.name === 'next'
          ? this.slider.scrollLeft + step * this.sliderItemOffset
          : this.slider.scrollLeft - step * this.sliderItemOffset;
    }
    this.setSlidePosition(this.slideScrollPosition);
  }

  onDotClick(event) {
    event.preventDefault;
    const slideScrollPosition = (event.currentTarget.dataset.index - 1) * this.sliderItemOffset;
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider && this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  connectedCallback() {
    this.productInfoWrapper = document.getElementById(`ProductInfo-${this.dataset.section}`);
    this.addOnProducts = this.productInfoWrapper.querySelectorAll('addon-product');
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();
    this.updateVariantStatuses();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.updateVariantMessaging();
      this.renderProductInfo();
      this.updateShareUrl();
      this.updateLegends();
      this.setAddonProductColor();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
    this.currentVariant.inventoryData = this.getVariantStockData().find((variant) => {
      return variant.id === this.currentVariant.id;
    });

    this.preorder =
      this.currentVariant.inventoryData.inventory_management === 'shopify' &&
      this.currentVariant.inventoryData.inventory_quantity <= 0 &&
      this.currentVariant.inventoryData.inventory_policy !== 'deny'
        ? true
        : false;
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    // Selected Variants Code
    const variantValues = this.currentVariant.options;
    const mediaGallery = document.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
    if (mediaGallery.hasAttribute('image-grouping-enabled')) {
      mediaGallery.querySelectorAll('[data-image-group]').forEach((el) => el.classList.add('hide-media'));
      variantValues.forEach((value) => {
        mediaGallery
          .querySelectorAll(`[data-image-group="${value}"]`)
          .forEach((el) => el.classList.remove('hide-media'));
      });
      mediaGallery.querySelectorAll('slider-component').forEach((slider) => {
        slider.initPages();
      });
    }

    const mediaModal = document.querySelector(`[id^="ProductModal-${this.dataset.section}"]`);
    if (mediaModal.hasAttribute('image-grouping-enabled')) {
      mediaModal.querySelectorAll('[data-image-group]').forEach((el) => el.classList.add('hide-media'));
      variantValues.forEach((value) => {
        mediaModal.querySelectorAll(`[data-image-group="${value}"]`).forEach((el) => el.classList.remove('hide-media'));
      });
      mediaModal.querySelectorAll('slider-component').forEach((slider) => {
        slider.initPages();
      });
    }
    // Selected Variants Code end

    const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);
    mediaGalleries.forEach((mediaGallery) => {
      setTimeout(() => {
        mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true);
      }, 250);
    });

    // const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    // if (!modalContent) return;
    // const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
    // modalContent.prepend(newMediaModal);
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateLegends() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    if (!fieldsets) return;
    fieldsets.forEach((fieldset) => {
      fieldset.querySelector('.form__label > span').textContent =
        Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked)?.value || '';
    });
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    this.klaviyoNotify = document.querySelector('klaviyo-notify');
    this.klaviyoNotify?.updateVariantId(this.currentVariant.id, this.currentVariant.available);
  }

  updateVariantStatuses() {
    const selectedOptionOneVariants = this.variantData.filter(
      (variant) => this.querySelector(':checked').value === variant.option1
    );
    const inputWrappers = [...this.querySelectorAll('.product-form__input')];
    inputWrappers.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected)
        .map((variantOption) => variantOption[`option${index + 1}`]);
      this.setInputAvailability(optionInputs, availableOptionInputsValue);
    });
  }

  updateVariantMessaging() {
    const variantMessaging = this.querySelector(`.variant-messaging-wrapper`).children;
    if (variantMessaging.length < 1) return;
    Array.from(variantMessaging).forEach((variantMessage) => {
      if (variantMessage.classList.contains(`variant-messaging-${this.currentVariant.id}`)) {
        variantMessage.classList.remove('visually-hidden');
      } else {
        variantMessage.classList.add('visually-hidden');
      }
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.innerText = input.getAttribute('value');
      } else {
        input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', input.getAttribute('value'));
      }
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;

    fetch(
      `${this.dataset.url}?variant=${requestedVariantId}&section_id=${
        this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section
      }`
    )
      .then((response) => response.text())
      .then((responseText) => {
        // prevent unnecessary ui changes from abandoned selections
        if (this.currentVariant.id !== requestedVariantId) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const second_destination = document.getElementById(`buy-price-${this.dataset.section}`);
        const source = html.getElementById(
          `price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const second_source = html.getElementById(
          `buy-price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuSource = html.getElementById(
          `Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
        const inventorySource = html.getElementById(
          `Inventory-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);

        const volumePricingSource = html.getElementById(
          `Volume-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );

        const pricePerItemDestination = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
        const pricePerItemSource = html.getElementById(
          `Price-Per-Item-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );

        const volumePricingDestination = document.getElementById(`Volume-${this.dataset.section}`);
        const qtyRules = document.getElementById(`Quantity-Rules-${this.dataset.section}`);
        const volumeNote = document.getElementById(`Volume-Note-${this.dataset.section}`);

        if (volumeNote) volumeNote.classList.remove('hidden');
        if (volumePricingDestination) volumePricingDestination.classList.remove('hidden');
        if (qtyRules) qtyRules.classList.remove('hidden');

        if (source && destination) destination.innerHTML = source.innerHTML;
        if (second_source && second_destination) second_destination.innerHTML = second_source.innerHTML;
        if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle('hidden', skuSource.classList.contains('hidden'));
        }

        if (volumePricingSource && volumePricingDestination) {
          volumePricingDestination.innerHTML = volumePricingSource.innerHTML;
        }

        if (pricePerItemSource && pricePerItemDestination) {
          pricePerItemDestination.innerHTML = pricePerItemSource.innerHTML;
          pricePerItemDestination.classList.toggle('hidden', pricePerItemSource.classList.contains('hidden'));
        }

        const price = document.getElementById(`price-${this.dataset.section}`);

        if (price) price.classList.remove('hidden');

        if (inventoryDestination) inventoryDestination.classList.toggle('hidden', inventorySource.innerText === '');

        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);
        this.toggleAddButton(
          addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
          addButtonUpdated.innerHTML
        );

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId,
            html,
            variant: this.currentVariant,
          },
          source: 'variant-selects',
        });
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span#Text-Add');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButton.innerHTML = text;
    } else {
      addButton.removeAttribute('disabled');
      // addButton.innerHTML = window.variantStrings.addToCart;
      if (text) addButton.innerHTML = text;
      if (this.preorder) {
        addButtonText.textContent = window.variantStrings.preorder;
      } else {
        addButtonText.innerHTML = window.variantStrings.addToCart;
      }
    }

    const preorderInput = productForm.querySelector('[name="properties[_preorder]"]');
    if (this.preorder && preorderInput) {
      preorderInput.value = 'true';
    } else if (!this.preorder && preorderInput) {
      preorderInput.value = 'false';
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
    const sku = document.getElementById(`Sku-${this.dataset.section}`);
    const pricePerItem = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
    const volumeNote = document.getElementById(`Volume-Note-${this.dataset.section}`);
    const volumeTable = document.getElementById(`Volume-${this.dataset.section}`);
    const qtyRules = document.getElementById(`Quantity-Rules-${this.dataset.section}`);

    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('hidden');
    if (inventory) inventory.classList.add('hidden');
    if (sku) sku.classList.add('hidden');
    if (pricePerItem) pricePerItem.classList.add('hidden');
    if (volumeNote) volumeNote.classList.add('hidden');
    if (volumeTable) volumeTable.classList.add('hidden');
    if (qtyRules) qtyRules.classList.add('hidden');
  }

  setAddonProductColor() {
    const colors = ['blue', 'black', 'orange', 'green'];
    const color = this.currentVariant.option1;
    if (colors.includes(color.toLowerCase()) && this.currentVariant.option2 == null) {
      Array.from(this.addOnProducts).forEach((addon) => {
        if (addon.getAttribute('selected') === 'selected') return;
        const addonSelect = addon.querySelector('select');
        if (addonSelect && [...addonSelect.options].find((o) => o.value === color)) {
          addonSelect.value = color;
          addonSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }

  getVariantStockData() {
    this.variantStockData = this.variantStockData || JSON.parse(this.querySelector('#Variant-Stock').textContent);
    return this.variantStockData;
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
        input.classList.remove('disabled');
      } else {
        input.classList.add('disabled');
      }
    });
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

customElements.define('variant-radios', VariantRadios);

class CardVariantRadios extends VariantRadios {
  constructor() {
    super();
    // 'quick-add-' | append: section.id | append: '-' | append: product.id
    this.productFormId = `#quick-add-${this.dataset.section}-${this.dataset.product}`;
  }
  connectedCallback() {}

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateVariantInput();
      this.updateLegends();
    }
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`${this.productFormId}`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}

customElements.define('card-variant-radios', CardVariantRadios);

class AddonProduct extends HTMLElement {
  static observedAttributes = ['selected', 'data-variant'];

  constructor() {
    super();
    this.section = this.dataset.section;
    this.button = this.querySelector('button[name="selected"]');
    this.addButton = document.querySelector('button[name="add"]');
    this.addButtons = document.querySelectorAll('button[name="add"]');

    this.productInfoWrapper = document.getElementById(`ProductInfo-${this.dataset.section}`);

    this.priceWrapper =
      document.querySelector('.product__form-wrapper')?.querySelector(`#Price-${this.section}`) || null;
    this.addQty = this.querySelector('button[name="plus"]');
    this.subtractQty = this.querySelector('button[name="minus"]');
    this.media = this.querySelector('.media > img');
    this.addonSelect = this.querySelector('addon-select');

    this.variantUpdateSubscriber = null;

    if (this.addonSelect) {
      this.addEventListener('change', this.setVariant.bind(this));
    }

    if (this.addButton && this.button) {
      this.addQuantity = this.addButton.querySelector(`.quantity--to-add`);
    }
    if (!this.button) return;
    this.addEventListener('click', this.handleButtonClick.bind(this));

    if (!this.addQty && !this.subtractQty) return;
    this.addQty.addEventListener('click', this.setQuantity.bind(this));
    this.subtractQty.addEventListener('click', this.setQuantity.bind(this));
  }

  connectedCallback() {
    this.getVariantData();
    this.getCurrentVariant();
    this.checkUrl();
    this.variantUpdateSubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event.source !== 'variant-selects') {
        return;
      }
      debounce(this.reAddAddonItems(), 300);
    });
  }

  disconnectedCallback() {
    if (this.variantUpdateSubscriber) {
      this.variantUpdateSubscriber();
    }
  }

  checkUrl() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    if (urlParams.has('addonProducts')) {
      const addonProducts = urlParams.get('addonProducts');
      if (addonProducts.split(',').includes(this.dataset.product) && this.querySelector('button[name="selected"]')) {
        this.setAttribute('selected', 'selected');
      }
      this.updateButtonQuantity();
      this.updateTotalPrice();
    }
  }

  reAddAddonItems() {
    this.addQuantity = this.addButton.querySelector('#quantity');
    if (this.getAttribute('selected').toLowerCase() === 'selected') {
      this.updateButtonQuantity();
      this.updateTotalPrice();
      // this.updateUrl();
    }
  }

  removeAddonItems() {
    Array.from(document.querySelectorAll('addon-product')).forEach((addon) => {
      addon.setAttribute('selected', false);
    });
  }

  handleButtonClick(e) {
    // console.log(e.target.tagName, e.target);
    if (
      e.target.tagName == 'SELECT' ||
      e.target.tagName == 'OPTION' ||
      (e.target.tagName == 'BUTTON' && e.target.getAttribute('name') !== 'selected')
    )
      return;
    if (this.getAttribute('selected') === 'selected') {
      this.setAttribute('selected', 'false');
    } else {
      this.setAttribute('selected', 'selected');
    }
    // this.updateUrl();
    this.updateButtonQuantity();
    this.updateTotalPrice();
  }

  updateUrl() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    if (this.getAddonProductIds().length === 0) {
      urlParams.delete('addonProducts');
    } else {
      urlParams.set('addonProducts', this.getAddonProductIds());
    }
    history.replaceState(null, null, `?${urlParams.toString()}`);
    // if (urlParams.has('addonProducts')) {
    //   urlParams.set('addonProducts', this.getAddonProductIds().join(','));
    //   console.log(urlParams.toString());
    // } else {
    //   urlParams.set('addonProducts', this.getAddonProductIds().join(','));
    //   console.log(urlParams.toString());
    // }
  }

  updateButtonQuantity() {
    if (!this.addButton) return;
    this.addButtons = document.querySelectorAll('button[name="add"]');
    const quantity =
      parseFloat(document.getElementById(`Quantity-${this.section}`).value) + this.getAddonQuantityTotal();
    for (let i = 0; i < this.addButtons.length; i++) {
      this.addButtons[i].querySelector(`.quantity--to-add`).textContent = `(${quantity})`;
      if (quantity > 1) {
        this.addButtons[i].querySelector(`.quantity--to-add`).classList.remove('hidden');
      } else {
        this.addButtons[i].querySelector(`.quantity--to-add`).classList.add('hidden');
      }
    }
  }

  updateTotalPrice(event = null) {
    // event == 'add' || 'subtract'
    if (!this.variantData && !this.priceWrapper && !this.currentVariant) return;
    const productPriceWrappers = document.querySelectorAll('.product__form-price');
    for (let i = 0; i < productPriceWrappers.length; i++) {
      // productPriceWrappers[i].querySelector('.price').classList.remove('price--on-sale');
      // productPriceWrappers[i].classList.add('price--with-addons');
    }
    const heroQuantity = parseInt(document.getElementById(`Quantity-${this.section}`).value);
    const regularPrice =
      parseFloat(
        this.productInfoWrapper
          .querySelector('.price-item--regular')
          .textContent.replace('$', '')
          .replaceAll(',', '')
          .trim()
      ) *
      100 *
      heroQuantity;
    const compareAtPrice =
      parseFloat(
        this.productInfoWrapper
          .querySelector('.price__sale')
          ?.querySelector('.price-item--regular')
          .textContent.replace('$', '')
          .replaceAll(',', '')
          .trim()
      ) *
        100 *
        heroQuantity || null;
    let newPrice = regularPrice;
    let newCompareAtPrice = compareAtPrice;
    const addonProductPriceTotal = this.getAddonPriceTotal();
    const addonProductCompareAtPrice = this.getAddonCompareAtPriceTotal();
    newPrice = regularPrice + addonProductPriceTotal;
    newCompareAtPrice = compareAtPrice + addonProductCompareAtPrice;

    for (let i = 0; i < productPriceWrappers.length; i++) {
      const regularPriceWrappers = productPriceWrappers[i].querySelectorAll('.price-item--regular');
      const salePriceWrappers = productPriceWrappers[i].querySelectorAll('.price-item--sale');
      if (newCompareAtPrice != 0) {
        for (let x = 0; x < regularPriceWrappers.length; x++) {
          regularPriceWrappers[x].textContent = formatCurrency(newCompareAtPrice);
        }
      } else {
        for (let x = 0; x < regularPriceWrappers.length; x++) {
          regularPriceWrappers[x].textContent = formatCurrency(newPrice);
        }
      }
      for (let x = 0; x < salePriceWrappers.length; x++) {
        salePriceWrappers[x].textContent = formatCurrency(newPrice);
      }
    }
  }

  getAddonProductIds() {
    const addOnProducts = this.productInfoWrapper.querySelectorAll('addon-product');
    let addonProductIds = [];
    for (let index = 0; index < addOnProducts.length; index++) {
      const element = addOnProducts[index];
      if (element.getAttribute('selected') === 'selected') {
        addonProductIds.push(element.dataset.product);
      }
    }
    return addonProductIds;
  }

  getAddonQuantityTotal() {
    const addOnProducts = this.productInfoWrapper.querySelectorAll('addon-product');
    let addonQuantityTotal = 0;
    for (let index = 0; index < addOnProducts.length; index++) {
      const element = addOnProducts[index];
      if (element.getAttribute('selected') === 'selected') {
        addonQuantityTotal += parseFloat(element.dataset.quantity);
      }
    }
    return addonQuantityTotal;
  }

  getAddonCompareAtPriceTotal() {
    const addOnProducts = this.productInfoWrapper.querySelectorAll('addon-product');
    let addonProductCompareAtPrice = 0;
    for (let index = 0; index < addOnProducts.length; index++) {
      const element = addOnProducts[index];
      if (element.getAttribute('selected') === 'selected') {
        addonProductCompareAtPrice += parseFloat(element.dataset.compareAtPrice);
      }
    }
    return addonProductCompareAtPrice === 'NaN' || null;
  }

  getAddonPriceTotal() {
    const addOnProducts = this.productInfoWrapper.querySelectorAll('addon-product');
    let addonProductPriceTotal = 0;
    for (let index = 0; index < addOnProducts.length; index++) {
      const element = addOnProducts[index];
      if (element.getAttribute('selected') === 'selected') {
        addonProductPriceTotal += parseFloat(element.dataset.totalPrice);
      }
    }
    return addonProductPriceTotal;
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]')?.textContent);
    return this.variantData;
  }

  getVariant() {
    return this.dataset.variant;
  }

  getCurrentVariant() {
    if (!this.variantData) return;
    this.currentVariant = this.variantData.find((variant) => {
      return variant.id === parseFloat(this.getVariant());
    });
  }

  setQuantity(e) {
    this.setAttribute('data-quantity', this.querySelector('input[name="quantity"]').value);
    this.setPrice(this.currentVariant);
    this.updateTotalPrice();
    this.updateButtonQuantity();
  }

  setMedia(variant) {
    if (variant.featured_image) {
      this.media.src = `${variant.featured_image.src}&width=200`;
      this.media.srcset = `${variant.featured_image.src}&width=200 200w`;
    }
  }

  setPrice(variant) {
    const priceWrapper = this.querySelector('.price');
    const regularPriceContainer = this.querySelectorAll('.price-item--regular');
    const salePriceContainer = this.querySelectorAll('.price-item--sale');
    const quantity = parseFloat(this.querySelector('input[name="quantity"]').value);
    if (variant.compare_at_price) {
      Array.from(salePriceContainer).forEach(
        (salePrice) => (salePrice.textContent = formatCurrency(variant.price * quantity))
      );

      Array.from(regularPriceContainer).forEach(
        (regularPrice) => (regularPrice.textContent = formatCurrency(variant.compare_at_price * quantity))
      );
      priceWrapper.classList.add('price--on-sale');
    } else {
      priceWrapper.classList.remove('price--on-sale');

      Array.from(regularPriceContainer).forEach(
        (regularPrice) => (regularPrice.textContent = formatCurrency(variant.price * quantity))
      );
    }
    this.dataset.totalPrice = variant.price * quantity;
    this.dataset.compareAtPrice = variant.compare_at_price * quantity;
  }

  setVariant(variant) {
    this.setMedia(variant);
    this.setPrice(variant);
    this.setAttribute('data-variant', variant.id);
    this.getCurrentVariant();
    this.updateTotalPrice();
  }
}

customElements.define('addon-product', AddonProduct);

class CartAddonProduct extends AddonProduct {
  constructor() {
    super();
    this.hasOnlyDefaultVariant = this.dataset.defaultVariant?.toLowerCase() === 'true' || false;
    this.preorder = this.dataset.preorder?.toLowerCase() === 'true' || false;
    this.loadingSpinner = this.querySelector('.loading__spinner');
    this.cartDrawer = document.querySelector('cart-drawer');
    this.cartPage = document.querySelector('.page-cart') ? true : false;
  }

  handleButtonClick(e) {
    super.handleButtonClick(e);
    if (this.hasOnlyDefaultVariant) {
      this.addToCart();
    }
  }

  updateTotalPrice() {
    return;
  }
  updateButtonQuantity() {
    return;
  }

  getAddonProductIds() {
    return;
  }

  checkUrl() {
    return;
  }

  updateUrl() {
    return;
  }

  addToCart() {
    if (!this.currentVariant.available) return;
    this.loadingSpinner.classList.remove('hidden');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', this.currentVariant.id);
    formData.append('quantity', 1);
    formData.append(
      'sections',
      this.cartDrawer.getSectionsToRender().map((section) => section.id)
    );

    if (this.preorder) {
      formData.append('properties[_preorder]', true);
    }
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.cartDrawer.handleErrorMessage(response.description);
          return;
        }
        this.cartDrawer.classList.remove('is-empty');
        this.setAttribute('selected', 'false');
        if (!this.cartPage) {
          this.cartDrawer.renderContents(response);
        } else {
          this.cartDrawer.renderContents(response, false);
          document.querySelector('cart-items').onCartUpdate();
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-variant' && oldValue != null) {
      this.getVariantData();
      this.getCurrentVariant();
      this.loadingSpinner.classList.remove('hidden');
      setTimeout(() => {
        this.addToCart();
      }, 250);
    }
  }
}

customElements.define('cart-addon-product', CartAddonProduct);

class AddonVariantSelects extends HTMLElement {
  constructor() {
    super();

    this.addonProduct = this.closest('addon-product') || this.closest('cart-addon-product');
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  onVariantChange(e) {
    e.preventDefault();
    this.updateOptions();
    this.updateMasterId();
    if (!this.currentVariant) return;
    this.addonProduct.setVariant(this.currentVariant);
    this.updateVariantMessaging();
  }

  updateVariantMessaging() {
    const variantMessaging = this.addonProduct.querySelector(`.variant-messaging-wrapper`)?.children;
    if (variantMessaging.length < 1) return;
    Array.from(variantMessaging).forEach((variantMessage) => {
      if (variantMessage.classList.contains(`variant-messaging-${this.currentVariant.id}`)) {
        variantMessage.classList.remove('visually-hidden');
      } else {
        variantMessage.classList.add('visually-hidden');
      }
    });
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('addon-variant-selects', AddonVariantSelects);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const recommendations = html.querySelector('product-recommendations');

          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
          }

          if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
            this.remove();
          }

          if (html.querySelector('.grid__item')) {
            this.classList.add('product-recommendations--loaded');
          }
        })
        .catch((e) => {
          console.error(e);
        });
    };

    new IntersectionObserver(handleIntersection.bind(this), { rootMargin: '0px 0px 400px 0px' }).observe(this);
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class CardSwatches extends HTMLElement {
  constructor() {
    super();
    this.sliderId = this.dataset.sliderId;
    this.slider = document.getElementById(`${this.dataset.sliderId}`);
    this.buttons = this.querySelectorAll('button');
    this.buttons.forEach((button) => button.addEventListener('click', this.handleClick.bind(this)));
  }

  connectedCallback() {
    // console.log(this.card);
  }

  handleClick(e) {
    this.removeSelected();
    e.target.setAttribute('aria-selected', 'true');
    const mediaId = e.target.dataset.mediaId;
    const slide = this.slider.querySelector(`#${mediaId}`);
    this.slider.scroll({ top: 0, left: slide.offsetLeft, behavior: 'instant' });
  }

  removeSelected() {
    this.buttons.forEach((button) => button.removeAttribute('aria-selected'));
  }
}

customElements.define('card-swatches', CardSwatches);

document.addEventListener('DOMContentLoaded', function () {
  if (window.location.search) {
    const urlParams = new URLSearchParams(window.location.search);
    const trigger = urlParams.has('bundles');
    const bundle_button = document.querySelector('.product-popup-modal__button');
    if (trigger && bundle_button) {
      bundle_button.click();
    }
  }
});

document.addEventListener('DOMContentLoaded', function () {
  const dimensionsBtn = document.querySelector('.product__anchor-inner');
  if (!dimensionsBtn) return;
  dimensionsBtn.addEventListener('click', function (e) {
    e.preventDefault();
    window.scrollTo({ top: document.querySelector(dimensionsBtn.getAttribute('href')).offsetTop, left: 0 });
  });
});
document.addEventListener('DOMContentLoaded', function () {
  var lazyVideos = [].slice.call(document.querySelectorAll('video.lazy'));

  if ('IntersectionObserver' in window) {
    var lazyVideoObserver = new IntersectionObserver(function (entries, observer) {
      entries.forEach(function (video) {
        if (video.isIntersecting) {
          for (var source in video.target.children) {
            var videoSource = video.target.children[source];
            if (typeof videoSource.tagName === 'string' && videoSource.tagName === 'SOURCE') {
              videoSource.src = videoSource.dataset.src;
            }
          }

          video.target.load();
          video.target.classList.remove('lazy');
          lazyVideoObserver.unobserve(video.target);
        }
      });
    });

    lazyVideos.forEach(function (lazyVideo) {
      lazyVideoObserver.observe(lazyVideo);
    });
  }
});
